import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  signal,
  WritableSignal,
  Signal,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  selectOutcomes,
  selectOutcomesTotal,
} from '../redux/outcome.selectors';

import { storeOutcomes } from '../redux/outcome.actions';
import { selectActiveSubmissionId } from '../redux/submission.selectors';
import {
  distinctUntilChanged,
  filter,
  Observable,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import environment from '../../../../environments/environment';

import { Outcomes } from '../models';
import { Document } from '@documents/models';

@Injectable({
  providedIn: 'root',
})
export class OutcomesService {
  private readonly _store = inject(Store);
  private readonly _http = inject(HttpClient);

  private outcomesSubject = new Subject<any[]>();
  private studyOutcomeDocumentsSubject = new Subject<any[]>();

  // Keep the original Observables for backward compatibility
  outcomes$: Observable<Outcomes[]> = this._store
    .select(selectOutcomes)
    .pipe(filter((outcomes): outcomes is Outcomes[] => !!outcomes));

  outcomesTotal$: Observable<number> = this._store
    .select(selectOutcomesTotal)
    .pipe(
      filter((total): total is number => total !== null && total !== undefined),
    );
  outcomeDocuments$: Observable<Document[]> =
    this.studyOutcomeDocumentsSubject.asObservable();

  // Create the corresponding signals
  $outcomes: Signal<Outcomes[]> = toSignal(this.outcomes$, {
    initialValue: [],
  });
  $outcomesTotal: Signal<number> = toSignal(this.outcomesTotal$, {
    initialValue: 0,
  });
  // For the subject-based Observable, create a writable signal
  private _$outcomeDocuments: WritableSignal<Document[]> = signal([]);
  // Create a readonly signal for external consumers
  get $outcomeDocuments(): Signal<Document[]> {
    return this._$outcomeDocuments;
  }

  constructor() {
    // Subscribe to the subject and update the signal when it emits
    this.studyOutcomeDocumentsSubject.subscribe((documents) => {
      this._$outcomeDocuments.set(documents);
    });
  }

  getOutcomes() {
    return this._store
      .select(selectActiveSubmissionId)
      .pipe(
        filter((workspaceId) => !!workspaceId),
        take(1),
        distinctUntilChanged(),
        switchMap((workspaceId: any) =>
          this.getStudyOutcomeDocuments(workspaceId),
        ),
        tap((outcomes: any) => {
          this.outcomesSubject.next(outcomes.data);
          this._store.dispatch(
            storeOutcomes({ outcomes: outcomes.data, total: outcomes.total }),
          );
        }),
      )
      .subscribe();
  }

  getStudyOutcomeDocuments(workspaceId: string): Observable<any[]> {
    this._http
      .get<
        any[]
      >(`${environment.irisApi}/workspace/${workspaceId}/outcome-documents/`)
      .subscribe((documents) => {
        this.studyOutcomeDocumentsSubject.next(documents);
        // Also update the signal directly
        this._$outcomeDocuments.set(documents);
      });
    return this.outcomeDocuments$;
  }
}
